import { Grid, withStyles } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import { Field } from 'react-final-form';
import { connect } from 'react-redux';
import { AuthState } from '../../../../auth/auth-reducer';
import { getClaim } from '../../../../claim';
import { PayeeTypeEnum } from '../../../../codes/payee-types';
import { PaymentType } from '../../../../codes/payment-types';
import {
    FormHeading,
    FullWidthSearchSelectField,
    FullWidthTextField,
    WhenFieldChanges,
} from '../../../../forms';
import { CurrencyMask, NumberMask } from '../../../../forms/input-masks';
import { composeValidators, maxValue, requiredValidator } from '../../../../forms/validations';
import { State } from '../../../../redux/root-reducer';
import { convertToNumber } from '../../../../shared-functions';
import { BankingDetailsDisplay } from '../../../../shared/banking-details-display';
import styles, { StyledComponent } from '../../../../styles/styles';
import {
    BeneficiaryDetails,
    ClaimDecision,
    getBeneficiaryCompanyName,
    getBeneficiaryEditName,
    getDecisions,
    getPaymentBeneficiaries,
} from '../../../reducer';
import { ClaimReference } from '../../../../claim/reducers/claim-reference-reducer';
import { getKeyTaxDirective, KeyTaxDirective } from '../../key-tax-directive/reducer';
import { LabeledValue } from '../../../../controls/labeled-value';
import { Nullable } from '../../../../shared-types';
import { ClaimType } from '../../../../claim/claim-types';
interface Props extends StyledComponent {
    beneficiaries: BeneficiaryDetails[];
    beneficiaryId: string;
    currentUser: AuthState;
    decisions: ClaimDecision[];
    edit?: boolean;
    claimReferences?: ClaimReference;
    isSarsPayment?: boolean;
    keyTaxDirectives: KeyTaxDirective[];
    bankIdex?: number;
    claimType: Nullable<ClaimType>;
}

const paymentItemView = ({
    beneficiaries,
    beneficiaryId,
    currentUser,
    decisions,
    edit,
    claimReferences,
    isSarsPayment,
    keyTaxDirectives,
    bankIdex,
    claimType,
}: Props) => {
    const beneficiary = beneficiaries.find(b => b.beneficiaryId === beneficiaryId);
    if (!beneficiary) {
        return null;
    }
    if (claimType === ClaimType.RADeath && beneficiary.raBankingDetails) {
        if (bankIdex !== undefined) {
            beneficiary.bankingDetails = beneficiary?.raBankingDetails[bankIdex];
        }
    }
    const taxDirective = keyTaxDirectives.find(c => c.dateReturned != null);
    let isDecisionSelected = false;
    return (
        <React.Fragment>
            {beneficiary.bankingDetails?.investmentIdType === 'Individual' && !isSarsPayment && (
                <FormHeading
                    text={`${edit ? 'Edit' : 'Add'} Payment - ${getBeneficiaryEditName(
                        beneficiary
                    )}`}
                />
            )}
            {beneficiary.bankingDetails?.investmentIdType !== 'Individual' && !isSarsPayment && (
                <FormHeading
                    text={`${edit ? 'Edit' : 'Add'} Payment - ${getBeneficiaryCompanyName(
                        beneficiary
                    )} `}
                />
            )}
            {isSarsPayment && <FormHeading text={`${edit ? 'Edit' : 'Add'} Payment - SARS `} />}

            <WhenFieldChanges
                field="decisionId"
                set=""
                to={(decisionId: string) => {
                    const decision = decisions.find(d => d.decisionId === decisionId);
                    if (isSarsPayment) {
                        isDecisionSelected = true;
                        return {
                            ...decision,
                            assessor: currentUser.name,
                            payeeType: getEnum(),
                            paymentType: PaymentType.Claim,
                            amountPayable: taxDirective?.amountReturned,
                        };
                    } else {
                        return {
                            ...decision,
                            assessor: currentUser.name,
                            payeeType: getEnum(),
                            paymentType: PaymentType.Claim,
                        };
                    }
                }}
            />
            <Grid container spacing={5} style={{ marginBottom: '120px' }}>
                <Grid item xs={6} lg={6}>
                    <FullWidthSearchSelectField
                        name="decisionId"
                        label="Policy"
                        validate={requiredValidator}
                        options={filterDecisions(decisions)}
                        disabled={edit}
                    />
                </Grid>
                <Field
                    name="."
                    subscription={{ value: true }}
                    component={({ input: { value: values } }) => {
                        setMaximumAmountPayable(beneficiary, values);
                        return null;
                    }}
                />
                <Grid item xs={6} lg={6}>
                    <FullWidthTextField
                        name="assessor"
                        label="Processed By"
                        defaultValue={currentUser.name}
                        disabled
                    />
                </Grid>
                <Grid item xs={6} lg={6}>
                    {isSarsPayment && (
                        <>
                            <FullWidthTextField
                                name="amountPayable"
                                mask={CurrencyMask}
                                parse={convertToNumber}
                                disabled
                            />
                            <Grid style={{ color: 'red' }}>
                                <LabeledValue
                                    label={'To change amount, go to tax directive screen'}
                                />
                            </Grid>
                        </>
                    )}

                    {!isSarsPayment && (
                        <Field
                            name="maxValue"
                            subscription={{ value: true }}
                            component={({ input: { value: amountPayable } }) => (
                                <FullWidthTextField
                                    name="amountPayable"
                                    mask={CurrencyMask}
                                    parse={convertToNumber}
                                    validate={composeValidators(
                                        requiredValidator,
                                        maxValue(amountPayable, 'maximum amount payable')
                                    )}
                                />
                            )}
                        />
                    )}
                </Grid>
                <Field
                    name="."
                    subscription={{ value: true }}
                    component={({ input: { value: values } }) => {
                        if (isSarsPayment && isDecisionSelected) {
                            const decision = decisions.find(
                                d => d.decisionId === taxDirective?.decisionId
                            );
                            if (values && decision && decision.sumAssured) {
                                let split =
                                    ((taxDirective?.amountReturned || 0) / decision.sumAssured) *
                                    100;
                                if (isNaN(Number(split))) {
                                    split = 0;
                                }
                                values.split = split.toFixed(2);
                            }
                            return null;
                        }
                        if (values) {
                            let split = ((values.amountPayable || 0) / values.sumAssured) * 100;
                            if (isNaN(Number(split))) {
                                split = 0;
                            }
                            values.split = split.toFixed(2);
                        }
                        return null;
                    }}
                />
                <Grid item xs={6} lg={6}>
                    <FullWidthTextField name="split" validate={requiredValidator} disabled />
                </Grid>
                <Grid item xs={6}>
                    <FullWidthTextField name="maxValue" label="Maximum amount payable" disabled />
                </Grid>
                {!claimReferences && (
                    <Grid item xs={6}>
                        <FullWidthTextField
                            name="caseNumber"
                            label="Case Number"
                            mask={NumberMask}
                            validate={requiredValidator}
                        />
                    </Grid>
                )}
                {!isSarsPayment &&
                    beneficiary.bankingDetails &&
                    claimType !== ClaimType.RADeath && (
                        <BankingDetailsDisplay
                            bankingDetails={beneficiary.bankingDetails}
                            raBankingDetails={beneficiary.raBankingDetails}
                            isCompany={isCompany()}
                        />
                    )}
                {!isSarsPayment &&
                    claimType === ClaimType.RADeath &&
                    beneficiary.raBankingDetails && (
                        <BankingDetailsDisplay
                            bankingDetails={beneficiary.bankingDetails}
                            raBankingDetails={beneficiary.raBankingDetails}
                            isCompany={isCompany()}
                            bankIdex={bankIdex}
                        />
                    )}
            </Grid>
        </React.Fragment>
    );

    function getEnum() {
        if (beneficiary?.bankingDetails?.investmentIdType === 'CompulsoryAnnuity') {
            return PayeeTypeEnum.CompulsoryAnnuity;
        } else if (beneficiary?.bankingDetails?.investmentIdType === 'Reinvestment') {
            return PayeeTypeEnum.ReInvestment;
        } else if (isSarsPayment) {
            return PayeeTypeEnum.PAYE;
        }
        return PayeeTypeEnum.Beneficiary;
    }

    function isCompany() {
        if (
            beneficiary?.bankingDetails?.investmentIdType === 'CompulsoryAnnuity' ||
            beneficiary?.bankingDetails?.investmentIdType === 'Reinvestment'
        ) {
            return true;
        }
    }
};

function filterDecisions(decisions: ClaimDecision[]) {
    const filteredDecisions: any = [];
    decisions.forEach(d => {
        filteredDecisions.push({
            value: d.decisionId,
            label: d.decisionLabel,
        });
    });
    return filteredDecisions;
}

function setMaximumAmountPayable(beneficiary: BeneficiaryDetails, values: any) {
    const existingPayments =
        (beneficiary.existingPayments &&
            beneficiary.existingPayments.filter(
                p => p.decisionId === values.decisionId && p.id !== values.id
            )) ||
        [];
    const totalPaymentAmount = existingPayments.map(p => p.amount).reduce((a, b) => a + b, 0);
    const maxAmount = Number((values.sumAssured - totalPaymentAmount).toFixed(2));

    if (
        !isNaN(maxAmount) &&
        maxAmount < values.maximumAmountPayable &&
        !(values.maximumAmountPayable - maxAmount <= 0.01)
    ) {
        values.maxValue = maxAmount.toFixed(2);
    } else {
        values.maxValue = values.maximumAmountPayable;
    }
}

const mapStateToProps = (state: State) => {
    const claim = getClaim(state);

    return {
        beneficiaries: getPaymentBeneficiaries(claim),
        currentUser: state.auth as AuthState,
        decisions: getDecisions(claim),
        claimReference: claim.references,
        keyTaxDirectives: getKeyTaxDirective(state),
        claimType: claim.claimType,
    };
};

export const PaymentFuneralItemView = connect(
    mapStateToProps,
    {}
)(withStyles(styles)(paymentItemView));
